/**
 * Settlements Queue API.
 *
 * Tickets between trading and settlements, plus the Salesforce reads that
 * back them (today's list, reference lookup, full record detail).
 */
import axios, { type AxiosInstance } from "axios";
import type { TransferDraft } from "@/lib/paymentInstructionsApi";
import { getSessionToken } from "@/lib/session";

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

function makeClient(): AxiosInstance {
  const client = axios.create({ baseURL: `${BASE_URL}/api/v1`, timeout: 30_000 });
  client.interceptors.request.use((config) => {
    const token = getSessionToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    try {
      const raw = localStorage.getItem("millbank-impersonation");
      if (raw) {
        const s = JSON.parse(raw)?.state;
        if (s?.target?.id && s?.startedAt) {
          const ageMs = Date.now() - new Date(s.startedAt).getTime();
          if (ageMs < (s.maxDurationSeconds || 3600) * 1000) {
            config.headers["X-Impersonate-User-Id"] = s.target.id;
            config.headers["X-Impersonate-Started-At"] = s.startedAt;
          }
        }
      }
    } catch {
      /* no impersonation */
    }
    return config;
  });
  return client;
}

const api = makeClient();

export function errMsg(e: any, fallback = "Something went wrong"): string {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d?.message) return d.message;
  return e?.message || fallback;
}

export type Flow = "payment" | "held" | "drawdown";
export type TicketStatus = "execution" | "release" | "completed";

export interface TicketSummary {
  kind?: "trade" | "payment";
  recordId?: string | null;
  reference?: string | null;
  recordName?: string | null;
  accountName?: string | null;
  accountId?: string | null;
  traderName?: string | null;
  tradeType?: string | null;
  valueDate?: string | null;
  sellAmount?: number | null;
  sellCurrency?: string | null;
  buyAmount?: number | null;
  buyCurrency?: string | null;
  clientRate?: number | null;
  amount?: number | null;
  currency?: string | null;
  amountGbp?: number | null;
  beneficiaryName?: string | null;
  beneficiaryId?: string | null;
  beneficiaryCount?: number | null;
  hasPayment?: boolean | null;
  dueDate?: string | null;
  paymentReference?: string | null;
  status?: string | null;
  incomingStatus?: string | null;
  outgoingStatus?: string | null;
  bankName?: string | null;
  nostro?: string | null;
  fmRegion?: string | null;
  fmCustomerId?: string | null;
}

export interface TicketEvent {
  kind: string;
  detail: Record<string, any>;
  actorName: string | null;
  createdAt: string | null;
}

export interface Ticket {
  id: string;
  flow: Flow;
  status: TicketStatus;
  reference: string | null;
  description: string | null;
  notes: string | null;
  urgent: boolean;
  extraCare: boolean;
  transfer: boolean;
  hasPayment: boolean;
  sfRecordType: string | null;
  sfRecordId: string | null;
  summary: TicketSummary;
  /** Drawdown tickets: the drawdown the trader asked for. */
  dd?: TicketDrawdown | null;
  createdBy: string | null;
  createdById: string | null;
  createdAt: string | null;
  claimedBy: string | null;
  claimedById: string | null;
  claimedAt: string | null;
  advancedBy: string | null;
  advancedById: string | null;
  advancedAt: string | null;
  completedBy: string | null;
  completedAt: string | null;
  flagged: boolean;
  flagReason: string | null;
  flaggedBy: string | null;
  flaggedAt: string | null;
  frozen: boolean;
  frozenReason: string | null;
  frozenAt: string | null;
  frozenMs: number;
  elapsedMs: number;
  clockRunning: boolean;
  breached: boolean;
  sfSyncStatus: string | null;
  sfSyncError: string | null;
  sfSyncedAt: string | null;
  archived: boolean;
  editedBy: string | null;
  editedAt: string | null;
  links: { salesforce: string | null; freemarket: string | null };
  canWork: boolean;
  events?: TicketEvent[];
  /** Released over the iFAST API: sending / awaiting (locked), settled. */
  apiState?: "sending" | "awaiting" | "settled" | null;
  apiNote?: string | null;
  /** Sent to Release without the API options: set up and released by hand only. */
  apiBypassedBy?: string | null;
  apiBypassedAt?: string | null;
  apiSentBy?: string | null;
  apiSentAt?: string | null;
  /** Looks like a GBP payment out of an iFAST account (the check decides). */
  apiHint?: boolean;
  /** Beneficiary bank details looked for in the payment's invoice. */
  invoiceCheck?: InvoiceCheck | null;
}

export type InvoiceCheckStatus =
  | "checking" | "validated" | "partial" | "not_found" | "unreadable" | "no_invoice" | "no_details" | "error";
export interface InvoiceCheckItem {
  label: string; value: string | null; found: boolean; in: string | null; how: string | null;
}
export type InvoiceMatch = "full" | "partial" | "none";
/** Visual check: page images (data URLs) with boxes as fractions of the page. */
export interface InvoiceVisualItem {
  key: string; kind: "account" | "code" | "name" | "line" | "postcode" | "country"; label: string; value: string;
  match: InvoiceMatch; in: string | null; located: boolean; where?: { file: string; page: number } | null;
}
export interface InvoiceVisual {
  paymentId: string; paymentName: string | null; beneficiaryName: string | null; checkedAt: string | null;
  items: InvoiceVisualItem[];
  files: { title: string; how: string | null; note: string | null;
           pages: { image: string; width: number; height: number; boxes: { item: string; x: number; y: number; w: number; h: number }[] }[] }[];
}

/** One name or address line looked for in the invoice. found / missing are its words (abbreviated, upper case). */
export interface InvoiceTextMatch {
  value: string | null; match: InvoiceMatch | null; in?: string | null; found?: string[]; missing?: string[]; how?: string;
}
export interface InvoiceAddressCheck {
  match: InvoiceMatch | null;
  lines: Array<InvoiceTextMatch & { label: string }>;
}
export interface InvoiceCheck {
  status: InvoiceCheckStatus;
  checkedAt?: string;
  note?: string;
  payments: Array<{
    paymentId: string; paymentName: string | null; beneficiaryId: string; beneficiaryName: string | null;
    status: InvoiceCheckStatus; account: InvoiceCheckItem; code: InvoiceCheckItem;
    /** Not on checks run before name and address were added. */
    name?: InvoiceTextMatch | null; address?: InvoiceAddressCheck | null;
    files: Array<{ title: string; how: string; chars: number }>;
  }>;
}

export type ApiScheme = "FPS" | "SEPA" | "INTERNATIONAL" | "OWN" | "";
export interface ApiReleasePayment {
  id: string; name: string | null; attempt: number;
  /** FPS (GBP to a UK bank), SEPA (EUR to a SEPA IBAN) or INTERNATIONAL. */
  scheme: ApiScheme;
  /** Which purpose code list it uses (a key of purposeGroups). */
  purposeGroup: string;
  /** Out of an iFAST account, so it can go over the API. False (e.g. Freemarket): released by hand, nothing here applies. */
  overApi?: boolean;
  /** Needs a code from iFAST's list: FPS, and SWIFT in AED / CNY / CNH or to a UAE or Chinese bank. Others send SUPP. */
  purposeRequired?: boolean;
  /** Paid out of a client's VIBAN: no ultimate debtor is sent (iFAST adds the client from the account). */
  fromViban?: boolean;
  /** What goes to iFAST as the purpose. */
  purpose?: string;
  /** The purpose code's description from iFAST's list ("Capital transfers"), if it's on one. */
  purposeLabel?: string;
  /** Schemes it can go by: ["SEPA", "INTERNATIONAL"] for EUR to the SEPA zone (SEPA by default), else just its own. */
  schemeOptions: ApiScheme[];
  /** The suggestion list key for each scheme option (only iFAST's lists have suggestions). */
  purposeGroups: Record<string, string>;
  /** SWIFT: the correspondent BIC read from the beneficiary's Correspondent Bank field (and the raw text). */
  correspondent: { bic: string; fromSalesforce: string } | null;
  /** SWIFT: "ABA 026009593" / "TRANSIT ..." sent as the first remittance line. */
  routing: string;
  beneficiary: string; sortCode: string; accountNumber: string;
  iban: string; bic: string; bankName: string; bankCountry: string;
  /** SHA / OUR / BEN, international only. */
  charges: string;
  amount: number; currency: string; reference: string; client: string;
  debitAccount: { name: string; currency?: string; sortCode: string; accountNumber: string };
  ok: boolean; problems: string[];
  /** Goes, but worth knowing (charges forced to SHA, no IBAN...). */
  warnings: string[];
  /** OWN: the receiving iFAST account (iFAST's name for it, our Salesforce bank account). */
  own?: { name: string; account: string; currency: string } | null;
  /** A New Payments transfer: the receiving Bank Payment, filled in here and made once it's seen. */
  receiving?: TransferDraft | null;
  /** The payment details as they'll be sent (null for internal transfers and payments that can't be built). */
  instruction?: PaymentInstruction | null;
  /** Our defaults (Salesforce plus our rules). */
  defaults?: PaymentInstruction | null;
  /** Fields changed from the defaults at Execution. */
  edited?: (keyof PaymentInstruction)[];
  /** Salesforce values that can't be edited here. */
  locked?: { amount: number; currency: string; debitAccountId: string | null; beneficiaryId: string | null };
  frozenAt?: string | null; frozenBy?: string | null;
}
/** One payment's details as sent to iFAST: set at Execution, approved at Release. */
export interface PaymentInstruction {
  scheme: "FPS" | "SEPA" | "INTERNATIONAL"; charges: string; purpose: string;
  beneficiaryName: string; address1: string; address2: string; country: string;
  sortCode: string; accountNumber: string; iban: string; bic: string;
  bankAddress1: string; bankAddress2: string; bankCountry: string;
  correspondentBic: string; correspondentAccount: string;
  reference: string; remittance1: string; remittance2: string; remittance3: string; remittance4: string;
  /** Ultimate debtor (the client the payment is for): sent on every SWIFT and SEPA payment, never on FPS. */
  udName: string; udStreet: string; udBuilding: string; udTown: string; udPostCode: string; udCountry: string;
}
export interface ApiPurposeGroup {
  key: string; label: string; default: string; codes: { code: string; label: string }[];
}
export interface ApiReleaseCheck {
  eligible: boolean; reason: string | null; reasons: string[];
  reference: string | null; client: string; payments: ApiReleasePayment[];
  count: number; total: number;
  /** The payments' currency, or "mixed". */
  currency: string;
  hours: string; advancedBy: string | null;
  notSettledMinutes: number; notSettledMinutesIntl: number;
  /** FPS default and list (older pages). */
  purpose: string; purposeCodes: { code: string; label: string }[];
  /** iFAST's purpose lists used on the ticket, offered as suggestions (FPS, SEPA, INTL_AE, INTL_CN). */
  purposeGroups: ApiPurposeGroup[];
  /** Payment details view: can I edit them (Execution, claimed by me). */
  editable?: boolean;
  /** Field labels. */
  fields?: Record<string, string>;
}

export interface QueueResponse {
  tickets: Ticket[];
  me: { id: string; name: string; canWork: boolean; canCreate: boolean; isAdmin: boolean; canApiRelease?: boolean; canProfit?: boolean };
  slaMinutes: number;
}

export interface CompletedResponse {
  tickets: Ticket[];
  total: number;
  page: number;
  pageSize: number;
}

export interface TodayResponse { trades: TicketSummary[]; payments: TicketSummary[]; alreadyQueued?: number }

export interface LookupResponse {
  found: boolean;
  sfRecordType?: string;
  sfRecordId?: string;
  summary?: TicketSummary;
  hasPayment?: boolean;
}

export interface TradeDetail {
  id: string; name: string | null; reference: string | null; tradeType: string | null; status: string | null;
  clientName: string | null; traderName: string | null; valueDate: string | null;
  sellAmount: number | null; sellCurrency: string | null; buyAmount: number | null; buyCurrency: string | null;
  clientRate: number | null; brokerRate: number | null; incomingNostro: string | null; outgoingNostro: string | null;
  lpSellAmount: number | null; lpBuyAmount: number | null; profit: number | null;
  liquidityProvider: string | null; lpReference: string | null;
  incomingStatus: string | null; outgoingStatus: string | null; createdBy: string | null; createdDate: string | null;
  salesforceUrl: string | null; fmRegion?: string | null; fmCustomerId?: string | null;
}

export interface PaymentDetail {
  id: string; name: string | null; clientName: string | null; amount: number | null; amountGbp: number | null;
  currency: string | null; nostro: string | null; bankName: string | null; reference: string | null;
  dueDate: string | null; status: string | null; sendConfirmation: boolean;
  beneficiaryId: string | null; beneficiaryName: string | null; salesforceUrl: string | null;
  fmRegion?: string | null; fmCustomerId?: string | null;
  /** Set when the payment is on a trade (settled through the trade). */
  tradeId?: string | null;
}

export interface BeneficiaryDetail {
  id: string; name: string | null; status: string | null; currency: string | null;
  street: string | null; city: string | null; stateProvince: string | null; postcode: string | null; country: string | null;
  bank: string | null; iban: string | null; bic: string | null; sortCode: string | null; aba: string | null;
  correspondentBank: string | null; paymentMethod: string | null; chargingOption: string | null;
  bankStreet: string | null; bankCity: string | null; bankStateProvince: string | null;
  bankPostcode: string | null; bankCountry: string | null; salesforceUrl: string | null;
  createdDate: string | null;
  firstPayment: boolean | null;
  paidBefore: number | null;
  files: SfFile[];
}

export interface SfFile {
  versionId: string; title: string | null; extension: string | null;
  size: number | null; createdDate: string | null;
}

export interface RecordDetail {
  recordType: string;
  recordId: string;
  trade: TradeDetail | null;
  payments: PaymentDetail[];
  beneficiaries: BeneficiaryDetail[];
  files: SfFile[];
  /** The invoice check on each open queue ticket for this record. */
  invoiceChecks?: Array<{ ticketId: string; reference: string | null; check: InvoiceCheck | null }>;
}

/** A daily figure with the best day before it and the 30-day weekday average. */
export interface VolumeCalibration {
  value: number;
  record: { value: number; date: string } | null;
  avg30: number | null;
}

export interface Metrics {
  from: string; to: string; slaMinutes: number;
  completed: number; avgSlaMins: number; medianSlaMins: number; worstSlaMins: number;
  breaches: number; breachPct: number; avgFirstTouchMins: number;
  byHour: { hour: number; count: number; avgSlaMins: number }[];
  byFlow: Record<string, number>;
  perUser: { name: string; completed: number; executed: number }[];
  open: { execution: number; release: number; flagged: number; frozen: number };
  oldestOpenMins: number;
  /** avgSlaMins is null on a day with no payment tickets (nothing timed). */
  daily: { date: string; completed: number; avgSlaMins: number | null; breaches: number; breachPct: number }[];
  /** Bank payments created in the period. errorRatePct = cancelled per non-cancelled one. */
  bankPayments: {
    from: string; to: string; valid: number; cancelled: number; errorRatePct: number;
    byHour: { hour: number; count: number }[];
  } | null;
  /** Payment tickets that were timed (SLA figures cover these only). */
  timedCompleted: number;
  paymentsExPayroll: number;
  payrollPayments: number;
  singleDay: boolean;
  volumes: Partial<Record<"payments" | "held" | "drawdown" | "bankPayments", VolumeCalibration>>;
  streak: {
    days: number;
    lastBreach: string | null;
    bestDay: { date: string; avgSlaMins: number; completed: number } | null;
    capped: boolean;
  };
}

export interface SearchHit {
  id: string;
  reference: string | null;
  description: string | null;
  flow: Flow;
  status: TicketStatus;
  where: string;
  accountName: string | null;
  withWhom: string | null;
  completedAt: string | null;
  createdAt: string | null;
  archived: boolean;
  urgent: boolean;
}

export interface TicketDrawdown { forwardRef: string; forwardId?: string; client?: string | null; currency: string; amount: number }

export interface NewTicket {
  flow: Flow;
  reference?: string | null;
  description?: string | null;
  notes?: string | null;
  urgent?: boolean;
  extraCare?: boolean;
  transfer?: boolean;
  hasPayment?: boolean;
  sfRecordType?: string | null;
  sfRecordId?: string | null;
  summary?: TicketSummary;
  /** New drawdown tickets: which forward, which of its currencies, how much. */
  dd?: { forwardRef: string; currency: string; amount: number };
}

/** A ticket raised since the last check, for desktop alerts. */
export interface NewTicketAlert {
  id: string; flow: Flow; reference: string | null; description: string | null; urgent: boolean;
  createdById: string | null; createdByName: string | null; createdAt: string | null;
}

/** A payment / drawdown ticket you raised, just completed by someone else. */
export interface CompletedTicketAlert {
  id: string; flow: Flow; reference: string | null; description: string | null;
  completedByName: string | null; completedAt: string | null;
}

export interface FxTestAccount { name: string; sortCode: string; accountNumber: string; currency: string; type: string }
export interface FxTest {
  id: string; requestId: string; createdAt: string | null; by: string | null;
  debit: FxTestAccount; credit: FxTestAccount; amount: number; currency: string; withQuote: boolean;
  quote: any; body: any; httpStatus: number | null; response: any; error: string | null;
  events: { at: string | null; payload: any }[];
}

export type ApiCurrencyMode = "off" | "on" | "dev";
export interface ApiCurrencies {
  currencies: { currency: string; mode: ApiCurrencyMode }[];
  log: { currency: string; from: string | null; to: string; by: string | null; at: string | null }[];
}

export const settlementsApi = {
  queue: () => api.get<QueueResponse>("/settlements/tickets").then((r) => r.data),
  newSince: (since?: string | null) =>
    api.get<{ tickets: NewTicketAlert[]; completed?: CompletedTicketAlert[]; now: string; me: string }>("/settlements/tickets/new", {
      params: since ? { since } : {},
    }).then((r) => r.data),
  create: (data: NewTicket) => api.post<{ ticket: Ticket }>("/settlements/tickets", data).then((r) => r.data.ticket),
  createMany: (tickets: NewTicket[]) =>
    api.post<{ tickets: Ticket[] }>("/settlements/tickets/bulk", { tickets }).then((r) => r.data.tickets),
  invoiceCheck: (id: string) =>
    api.post<{ ticket: Ticket }>(`/settlements/tickets/${id}/invoice-check`, null, { timeout: 180_000 }).then((r) => r.data.ticket),
  /** The invoice's pages with each checked item's place boxed (built on request). */
  invoiceVisual: (id: string, paymentId: string) =>
    api.get<InvoiceVisual>(`/settlements/tickets/${id}/invoice-visual`, { params: { paymentId }, timeout: 180_000 }).then((r) => r.data),
  refreshRecord: (id: string) =>
    api.post<{ ticket: Ticket }>(`/settlements/tickets/${id}/refresh-record`).then((r) => r.data.ticket),
  move: (id: string, flow: Flow) =>
    api.post<{ ticket: Ticket }>(`/settlements/tickets/${id}/move`, { flow }).then((r) => r.data.ticket),
  edit: (id: string, data: Partial<NewTicket>) =>
    api.put<{ ticket: Ticket }>(`/settlements/tickets/${id}`, data).then((r) => r.data.ticket),
  remove: (id: string) => api.delete(`/settlements/tickets/${id}`).then(() => true),
  claim: (id: string, override = false) =>
    api.post<{ ticket: Ticket }>(`/settlements/tickets/${id}/claim`, { override }).then((r) => r.data.ticket),
  unclaim: (id: string) => api.post<{ ticket: Ticket }>(`/settlements/tickets/${id}/unclaim`).then((r) => r.data.ticket),
  /** bypassApi: to Release without the API details or checks (released by hand only). */
  advance: (id: string, bypassApi = false) =>
    api.post<{ ticket: Ticket }>(`/settlements/tickets/${id}/advance`, bypassApi ? { bypassApi: true } : undefined).then((r) => r.data.ticket),
  complete: (id: string, paymentIds?: string[], disburseFirst?: boolean, profit?: ProfitChoice) =>
    api.post<{ ticket: Ticket }>(`/settlements/tickets/${id}/complete`,
      paymentIds || disburseFirst || profit
        ? { ...(paymentIds ? { paymentIds } : {}), ...(disburseFirst ? { disburseFirst } : {}), ...(profit ? { profit } : {}) }
        : undefined,
      { timeout: 60_000 },
    ).then((r) => r.data.ticket),
  /** Before Complete: ask about the profit on this trade? */
  profitCheck: (id: string) =>
    api.get<ProfitCheck>(`/settlements/tickets/${id}/profit-check`, { timeout: 60_000 }).then((r) => r.data),
  profits: (day?: string) =>
    api.get<ProfitsOverview>("/settlements/profits", { params: day ? { day } : {} }).then((r) => r.data),
  profitLiveAccounts: () =>
    api.get<{ accounts: Record<string, OpsAccount[]> }>("/settlements/profits/live-accounts", { timeout: 60_000 }).then((r) => r.data.accounts),
  profitSetOpsAccounts: (accounts: Record<string, { sortCode: string; accountNumber: string } | null>) =>
    api.put<{ opsAccounts: Record<string, OpsAccount> }>("/settlements/profits/ops-accounts", { accounts }, { timeout: 60_000 })
      .then((r) => r.data.opsAccounts),
  profitCheckCancelled: (day: string) =>
    api.post<CancelCheck>("/settlements/profits/check-cancelled", { day }, { timeout: 120_000 }).then((r) => r.data),
  profitRefund: (id: string, method: string, code: string) =>
    api.post<{ status: string; error?: string | null }>(`/settlements/profits/${id}/refund`, { method, code }, { timeout: 90_000 })
      .then((r) => r.data),
  profitDismissRefund: (id: string) => api.post(`/settlements/profits/${id}/refund-dismiss`).then(() => true),
  profitRetry: (id: string, method: string, code: string) =>
    api.post<{ status: string; error?: string | null }>(`/settlements/profits/${id}/retry`, { method, code }, { timeout: 90_000 })
      .then((r) => r.data),
  profitManual: (id: string) => api.post(`/settlements/profits/${id}/manual`).then(() => true),
  profitLeftoversSwept: (moveIds: string[]) =>
    api.post<{ marked: number }>("/settlements/profits/leftovers/swept", { moveIds }).then((r) => r.data.marked),
  profitSell: (body: { day: string; actions: Record<string, "sell" | "keep">; expected: Record<string, number>; method: string; code: string }) =>
    api.post<{ results: Record<string, { state: string; amount?: number; gbp?: number; message?: string | null }> }>(
      "/settlements/profits/sell", body, { timeout: 120_000 }).then((r) => r.data.results),
  profitCodeMethods: () =>
    api.get<{ totp: boolean; sms: boolean; phone: string }>("/settlements/profits/code-methods").then((r) => r.data),
  profitSendSms: () => api.post("/settlements/profits/code/sms").then((r) => r.data),
  /** Held trade: can the client cover the trade payment, and if not, can it be disbursed first. */
  heldCheck: (id: string) =>
    api.get<HeldCheck>(`/settlements/tickets/${id}/held-check`, { timeout: 60_000 }).then((r) => r.data),
  apiReleaseCheck: (id: string) =>
    api.get<ApiReleaseCheck>(`/settlements/tickets/${id}/api-release`, { timeout: 60_000 }).then((r) => r.data),
  /** Each payment goes as its details (set at Execution) say. */
  apiRelease: (id: string, body: { method: string; code: string; expectedCount: number; expectedTotal: number;
    receiving?: Record<string, Record<string, unknown>> }) =>
    api.post<{ ticket: Ticket; sent: number; refused: number; message: string }>(
      `/settlements/tickets/${id}/api-release`, body, { timeout: 120_000 }).then((r) => r.data),
  /** The iFAST payment details on a payment ticket (editable in Execution). */
  instructions: (id: string) =>
    api.get<ApiReleaseCheck>(`/settlements/tickets/${id}/instructions`, { timeout: 60_000 }).then((r) => r.data),
  saveInstruction: (id: string, paymentId: string, fields: PaymentInstruction) =>
    api.put<ApiReleaseCheck>(`/settlements/tickets/${id}/instructions/${paymentId}`, { fields }, { timeout: 60_000 }).then((r) => r.data),
  resetInstruction: (id: string, paymentId: string) =>
    api.delete<ApiReleaseCheck>(`/settlements/tickets/${id}/instructions/${paymentId}`, { timeout: 60_000 }).then((r) => r.data),
  /** Release -> Execution, with a reason. */
  sendBack: (id: string, reason: string) =>
    api.post<{ ticket: Ticket }>(`/settlements/tickets/${id}/send-back`, { reason }).then((r) => r.data.ticket),
  /** iFAST's BIC directory (correspondent banks). */
  /** Super Admin: which currencies go over the API (on / dev = admins only / off). */
  apiCurrencies: () => api.get<ApiCurrencies>("/settlements/api-currencies").then((r) => r.data),
  saveApiCurrencies: (modes: Record<string, ApiCurrencyMode>) =>
    api.put<ApiCurrencies>("/settlements/api-currencies", { modes }).then((r) => r.data),
  /** Super Admin: FX test with "side": "SELL" (exact amount out of the debit account). */
  fxTestHome: () => api.get<{ accounts: FxTestAccount[]; recent: FxTest[]; max: number }>("/settlements/fx-test").then((r) => r.data),
  fxTestQuote: (debit: string, credit: string, amount: string) =>
    api.post<{ sell: string; buy: string; amount: string; quote: any }>("/settlements/fx-test/quote", { debit, credit, amount }).then((r) => r.data),
  fxTestSend: (debit: string, credit: string, amount: string, withQuote: boolean) =>
    api.post<FxTest>("/settlements/fx-test", { debit, credit, amount, withQuote }, { timeout: 60_000 }).then((r) => r.data),
  fxTestGet: (id: string) => api.get<FxTest>(`/settlements/fx-test/${id}`).then((r) => r.data),
  apiBic: (bic: string) =>
    api.get<{ bic: string | null; problem: string | null; bankName?: string; address?: string[]; country?: string; chaps?: boolean | null }>(
      "/settlements/api-release/bic", { params: { bic } }).then((r) => r.data),
  apiCodeMethods: () =>
    api.get<{ totp: boolean; sms: boolean; phone: string }>("/settlements/api-release/code-methods").then((r) => r.data),
  apiSendSms: () => api.post("/settlements/api-release/code/sms").then((r) => r.data),
  /** Check payment status: every in-flight API payment against its account, now. */
  apiPoll: () =>
    api.post<{ watched?: number; seen?: number; tickets?: Record<string, number>; skipped?: string; message?: string }>(
      "/settlements/api-release/poll", undefined, { timeout: 90_000 }).then((r) => r.data),
  beneficiaryPayments: (id: string) =>
    api.get<{ payments: BeneficiaryPayment[] }>(`/settlements/tickets/${id}/beneficiary-payments`).then((r) => r.data.payments),
  retrySync: (id: string, disburseFirst?: boolean) =>
    api.post<{ ticket: Ticket }>(`/settlements/tickets/${id}/retry-sync`, disburseFirst ? { disburseFirst } : undefined)
      .then((r) => r.data.ticket),
  flag: (id: string, reason: string) =>
    api.post<{ ticket: Ticket }>(`/settlements/tickets/${id}/flag`, { reason }).then((r) => r.data.ticket),
  unflag: (id: string, target: "execution" | "release") =>
    api.post<{ ticket: Ticket }>(`/settlements/tickets/${id}/unflag`, { target }).then((r) => r.data.ticket),
  freeze: (id: string, reason: string) =>
    api.post<{ ticket: Ticket }>(`/settlements/tickets/${id}/freeze`, { reason }).then((r) => r.data.ticket),
  unfreeze: (id: string) => api.post<{ ticket: Ticket }>(`/settlements/tickets/${id}/unfreeze`).then((r) => r.data.ticket),
  setNotes: (id: string, notes: string) =>
    api.put<{ ticket: Ticket }>(`/settlements/tickets/${id}/notes`, { notes }).then((r) => r.data.ticket),
  setUrgent: (id: string, urgent: boolean) =>
    api.put<{ ticket: Ticket }>(`/settlements/tickets/${id}/urgent`, { urgent }).then((r) => r.data.ticket),
  events: (id: string) => api.get<{ events: TicketEvent[] }>(`/settlements/tickets/${id}/events`).then((r) => r.data.events),
  completed: (params: { page?: number; pageSize?: number; q?: string; dateFrom?: string; dateTo?: string; includeArchived?: boolean }) =>
    api.get<CompletedResponse>("/settlements/tickets/completed", { params }).then((r) => r.data),
  search: (q: string) =>
    api.get<{ query: string; results: SearchHit[] }>("/settlements/tickets/search", { params: { q } }).then((r) => r.data),
  today: () => api.get<TodayResponse>("/settlements/salesforce/today").then((r) => r.data),
  lookup: (reference: string) =>
    api.get<LookupResponse>("/settlements/salesforce/lookup", { params: { reference } }).then((r) => r.data),
  /** Status Completed + debit Transaction in Salesforce. */
  markPaid: (paymentId: string) =>
    api.post<{ paymentId: string; name: string | null; statusChanged: boolean; debitTransactionId: string | null; debitCreated: boolean; ticketsCompleted?: string[] }>(
      `/settlements/payments/${paymentId}/mark-paid`,
    ).then((r) => r.data),
  record: (recordType: string, recordId: string) =>
    recordType === "ref"
      ? api.get<RecordDetail>("/settlements/salesforce/record-by-reference", { params: { reference: recordId } }).then((r) => r.data)
      : api.get<RecordDetail>(`/settlements/salesforce/record/${recordType}/${recordId}`).then((r) => r.data),
  fileUrl: (versionId: string) => `${BASE_URL}/api/v1/settlements/salesforce/file/${versionId}`,
  downloadFile: async (versionId: string, filename: string) => {
    const res = await api.get(`/settlements/salesforce/file/${versionId}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data as Blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },
  metrics: (params: { dateFrom?: string; dateTo?: string; days?: number } = {}) =>
    api.get<Metrics>("/settlements/metrics", { params }).then((r) => r.data),
  tvRecipients: () => api.get<TvRecipients>("/settlements/tv-report/recipients").then((r) => r.data),
  setTvRecipients: (userIds: string[]) =>
    api.put<TvRecipients>("/settlements/tv-report/recipients", { userIds }).then((r) => r.data),
  tvTest: () => api.post<{ date: string; results: string[] }>("/settlements/tv-report/test", {}, { timeout: 120_000 }).then((r) => r.data),
  tvPdf: async () => {
    const r = await api.get("/settlements/tv-report/pdf", { responseType: "blob", timeout: 120_000 });
    const name = /filename="([^"]+)"/.exec(String(r.headers["content-disposition"] || ""))?.[1] || "Settlements standup.pdf";
    const url = URL.createObjectURL(r.data as Blob);
    const a = document.createElement("a");
    a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
  },
};

export interface TvRecipients {
  emailMode: string;
  users: { id: string; name: string; email: string; allowlisted: boolean; selected: boolean }[];
}

// ── Shared formatting ───────────────────────────────────────────

export const FLOW_LABEL: Record<Flow, string> = {
  payment: "Payment",
  held: "Held",
  drawdown: "Drawdown",
};

export function money(amount?: number | null, currency?: string | null): string {
  if (amount === null || amount === undefined) return "";
  const n = Number(amount);
  const formatted = n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency ? `${formatted} ${currency}` : formatted;
}

export function duration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h) return `${h}h ${m}m`;
  return `${m}m ${String(s).padStart(2, "0")}s`;
}

export function formatDate(iso: string | null | undefined, withTime = false): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
    ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  });
}

export function formatTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

export const EVENT_LABEL: Record<string, string> = {
  created: "Ticket raised",
  claimed: "Claimed",
  unclaimed: "Released back to the queue",
  advanced: "Moved to Release",
  completed: "Completed",
  flagged: "Flagged for review",
  unflagged: "Flag removed",
  frozen: "Frozen",
  unfrozen: "Unfrozen",
  note: "Note updated",
  urgent: "Marked urgent",
  not_urgent: "Urgent removed",
  release_override: "Release rule overridden by admin",
  salesforce_sync: "Salesforce update",
  disburse_first: "Chose to disburse before paying (client short)",
  profit: "Profit decided on completion",
  profit_sent: "Profit sent to the ops account",
  profit_failed: "Profit couldn't be sent",
  profit_refund: "Profit sent back (trade cancelled)",
  profit_manual: "Profit handled by hand",
  edited: "Ticket edited",
  flow_changed: "Moved to another flow",
  record_linked: "Salesforce record attached",
  deleted: "Ticket deleted",
  instruction_edited: "Payment details edited",
  instruction_reset: "Payment details reset to defaults",
  sent_back: "Sent back to Execution",
};


/** A held trade's payment against the client's balance in that bank account. */
export interface HeldCheck {
  trade?: string; short: boolean; canDisburseFirst: boolean; reason: string | null;
  balance?: number; tradeAmount?: number; creditAmount?: number; profit?: number | null; fee?: number;
  balanceAfter?: number; currency?: string; bankAccount?: string;
}

/** Profit extraction: one currency the profit can be in, where it comes from and goes. */
export interface ProfitOption {
  currency: string; scheme: "OWN" | "FX_OWN"; problem: string | null;
  from: { id: string; name: string }; to: { name: string; currency: string } | null;
}

/** Before Complete on a trade whose two accounts are at iFAST and client rate isn't 1. */
export interface ProfitCheck {
  show: boolean; permitted: boolean; reason: string | null;
  kind?: "landing" | "fx"; trade?: string; tradeId?: string; client?: string; clientRate?: number;
  tradeProfit?: number | null; suggested?: number | null; options?: ProfitOption[];
  sell?: { amount: number; currency: string }; buy?: { amount: number; currency: string };
}

/** Sent with Complete: what to do with the profit. */
export type ProfitChoice =
  | { mode: "manual" }
  | { mode: "send"; currency: string; amount: string; method: string; code: string };

export interface OpsAccount { name: string; sortCode: string; accountNumber: string; iban?: string; currency: string }

export type ProfitStatus = "waiting_sf" | "sending" | "sent" | "settled" | "failed" | "manual";

export interface ProfitRow {
  id: string; trade: string | null; tradeId: string; ticketId: string; reference: string | null; client: string | null;
  kind: "landing" | "fx"; mode: "send" | "manual"; status: ProfitStatus; error: string | null;
  currency: string | null; amount: number | null; tradeProfit: number | null; scheme: "OWN" | "FX_OWN" | null;
  from: string | null; to: string | null; gbp: number | null; day: string; by: string | null; at: string | null;
  sentAt: string | null; settledAt: string | null; tradeCancelledAt: string | null;
  refund: "refund_sent" | "refunded" | "refund_failed" | null; refundError: string | null; refundDismissedBy: string | null;
}

export interface ProfitSale {
  id: string; action: "sell" | "keep"; amount: number; state: "kept" | "selling" | "sold" | "failed";
  gbp: number | null; sold: number | null; error: string | null; by: string | null; at: string | null;
}

export interface ProfitFigures {
  confirmed: number; pending: number; refunded: number; net: number; sold: number; kept: number;
  gbpFromSales: number; toSell: number; count: number; sales: ProfitSale[];
}

export interface CancelCheck { at: string; by: string; checked: number; cancelled: number }

/** What conversions left behind in one account and currency, to sell by hand in iFAST. */
export interface ProfitLeftover {
  account: string; sortCode: string; accountNumber: string; currency: string; amount: number;
  count: number; moveIds: string[]; since: string | null; where: "client" | "ops";
}

export interface ProfitsOverview {
  day: string; today: string; isAdmin: boolean;
  figures: Record<"GBP" | "USD" | "EUR", ProfitFigures>;
  converted: ProfitRow[]; profits: ProfitRow[]; attention: ProfitRow[];
  openDays: { day: string; toSell: Record<string, number> }[];
  opsAccounts: Record<string, OpsAccount>;
  cancelCheck: CancelCheck | null;
  leftovers?: ProfitLeftover[];
}

/** A beneficiary payment on a trade (Payment__c.Outgoing_Payments__c = trade). */
export interface BeneficiaryPayment {
  id: string; name: string; beneficiary: string; amount: number; currency: string; reference: string; status: string;
}
